import React, { useState } from "react";
import CartPanel from "./CartPanel";
import { getPurchaseItems } from "../utils/purchaseItems";

const PurchaseDetailsModal = ({
  isOpen,
  onClose,
  purchase,
  products,
  openProductModal,
  handleRemoveItem,
  canEditItems = true,
  pendingItemDiscounts,
  pendingOrderDiscount,
  onResumePendingItemDiscount,
  onResumePendingOrderDiscount,
  footerActions,
  getItemPricing,
  overlayZClassName = 'z-[950]',
}) => {
  const [showFullClientDetails, setShowFullClientDetails] = useState(false);
  if (!isOpen || !purchase) return null;

  const purchaseItems = getPurchaseItems(purchase);
  const itemsSubtotal =
    typeof purchase.itemsSubtotal === 'number'
      ? purchase.itemsSubtotal
      : purchaseItems.reduce((sum, item) => {
          if (!item || item.quantity <= 0) return sum;
          const explicitSubtotal = Number(item.subtotal);
          if (Number.isFinite(explicitSubtotal) && explicitSubtotal >= 0) {
            return sum + Math.floor(explicitSubtotal);
          }
          const unit = (item.price || 0) - (item.discount || 0);
          return sum + Math.floor(unit * item.quantity);
        }, 0);
  const percentNum = Number(purchase.orderDiscountPercentage);
  const orderDiscountPercentage = Number.isFinite(percentNum) ? percentNum : null;
  const amountNum = Number(purchase.orderDiscountAmount);
  const orderDiscountAmount = Number.isFinite(amountNum) ? amountNum : null;
  const totalAmount = Number(purchase.totalAmount || 0);
  const getVarietyForItem = (item) =>
    products
      ?.flatMap((product) => product.Varieties || [])
      .find((variety) => variety.id === item.varietyId);
  const modalFooterActions = footerActions ? (
    <div className="mt-2 [&_button]:h-11 [&_button]:min-h-[44px] [&_button]:min-w-[44px] [&_button]:px-4 [&_button]:py-2 [&_button]:text-sm [&_button]:font-semibold [&_button]:inline-flex [&_button]:items-center [&_button]:justify-center">
      {footerActions}
    </div>
  ) : null;

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex items-start justify-center ${overlayZClassName} px-3 py-4 sm:px-4 sm:py-6 overflow-y-auto overscroll-contain`}
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden border border-gray-100 max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Venta</p>
            <h1 className="text-lg font-semibold text-gray-900">Detalles de la compra</h1>
            <p className="text-xs text-gray-500 mt-1">
              {purchase?.id ? `Venta #${purchase.id}` : 'Venta nueva'} · {new Date(purchase.createdAt).toLocaleString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        <div className="p-6 flex-1 min-h-0 flex flex-col gap-6 overflow-hidden">
          {purchase.Client?.rut && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm shrink-0">
              <div className="flex items-center justify-between cursor-pointer" onClick={() => setShowFullClientDetails((prev) => !prev)}>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Cliente</p>
                  <h2 className="text-lg font-semibold text-gray-900">{purchase.Client?.name}</h2>
                </div>
                <span className="text-sm text-gray-500">{showFullClientDetails ? '▲' : '▼'}</span>
              </div>
              {showFullClientDetails && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                  <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                    <span className="font-semibold text-gray-700">RUT:</span> {purchase.Client?.rut}
                  </div>
                  <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                    <span className="font-semibold text-gray-700">Email:</span> {purchase.Client?.email}
                  </div>
                  <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                    <span className="font-semibold text-gray-700">Teléfono:</span> {purchase.Client?.phone}
                  </div>
                  <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                    <span className="font-semibold text-gray-700">Ciudad:</span> {purchase.Client?.city}
                  </div>
                  <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 md:col-span-2">
                    <span className="font-semibold text-gray-700">Dirección:</span> {purchase.Client?.address}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 shrink-0">Productos comprados</h2>
            <CartPanel
              items={purchaseItems}
              getVariety={getVarietyForItem}
              onItemClick={(item, variety) => {
                if (!variety) return;
                const priceEntry =
                  variety?.VarietyPrices?.find(
                    (price) => Number(price.id) === Number(item.varietyPriceId)
                  ) ||
                  (Number.isInteger(item.priceIndex)
                    ? variety?.VarietyPrices?.[item.priceIndex]
                    : null);
                openProductModal?.(variety, priceEntry, item);
              }}
              onRemoveItem={handleRemoveItem}
              canEdit={canEditItems}
              itemsSubtotal={itemsSubtotal}
              orderDiscountPercentage={orderDiscountPercentage}
              orderDiscountAmount={orderDiscountAmount}
              totalAmount={totalAmount}
              pendingItemDiscounts={pendingItemDiscounts}
              pendingOrderDiscount={pendingOrderDiscount}
              onResumePendingItemDiscount={onResumePendingItemDiscount}
              onResumePendingOrderDiscount={onResumePendingOrderDiscount}
              getItemPricing={getItemPricing}
              panelClassName="bg-white border border-gray-200 rounded-t-lg p-4 flex-1 min-h-[12rem] overflow-y-auto overscroll-contain"
              footerClassName="shrink-0 border border-t-0 border-gray-300 bg-gray-100 rounded-b-lg px-3 py-3 flex flex-col gap-2"
              showSummaryBreakdown={false}
              footerActions={modalFooterActions}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseDetailsModal;
